import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Shield, AlertTriangle, Store, Users } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { getApiUrl } from '../utils/api';

interface ServiceFormData {
  id: string;
  name: string;
  description: string;
  category: 'TATTOO' | 'PIERCING' | 'CONSULTATION' | 'AFTERCARE' | 'REMOVAL' | 'JEWELRY';
  subcategory?: string;
  basePrice: number;
  estimatedDuration: number;
  sessionCount?: number;
  isActive: boolean;
  onlineBookingEnabled: boolean;
  depositRequired: boolean;
  depositType?: 'FIXED' | 'PERCENTAGE';
  depositAmount?: number;
  maxAdvanceBookingDays?: number;
  createdAt: string;
  updatedAt: string;
}

interface ShopAssociation {
  id: string;
  name: string;
  city: string;
  isOffered: boolean;
  isAvailable: boolean;
}

interface PractitionerAssociation {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isOffered: boolean;
  isAvailable: boolean;
}

export function ServiceEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [service, setService] = useState<ServiceFormData | null>(null);
  const [shops, setShops] = useState<ShopAssociation[]>([]);
  const [practitioners, setPractitioners] = useState<PractitionerAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    if (id) {
      fetchService(id);
      fetchServiceShops(id);
      fetchServicePractitioners(id);
    }
  }, [id]);

  const fetchService = async (serviceId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(getApiUrl(`/api/admin/services/${serviceId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setService(data.data.service);
      } else {
        setError(data.message || 'Échec du chargement du service');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Fetch service error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceShops = async (serviceId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/services/${serviceId}/shops`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setShops(data.data.shops || []);
        }
      }
    } catch (error) {
      console.error('Fetch service shops error:', error);
    }
  };

  const fetchServicePractitioners = async (serviceId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/services/${serviceId}/practitioners`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPractitioners(data.data.practitioners || []);
        }
      }
    } catch (error) {
      console.error('Fetch service practitioners error:', error);
    }
  };

  const handleInputChange = (field: keyof ServiceFormData, value: any) => {
    if (!service) return;
    
    setService(prev => prev ? {
      ...prev,
      [field]: value
    } : prev);
    
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!service) return;
    
    try {
      setSaving(true);
      setError(null);
      setErrorDetails([]);
      const token = localStorage.getItem('token');
      
      const response = await fetch(getApiUrl(`/api/admin/services/${service.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(service),
      });

      const data = await response.json();
      if (data.success) {
        setHasUnsavedChanges(false);
        navigate('/services');
      } else {
        setError(data.message || 'Échec de la sauvegarde du service');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Save service error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?')) {
        navigate('/services');
      }
    } else {
      navigate('/services');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement du service...</span>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">
              {error || 'Service non trouvé'}
            </span>
          </div>
        </div>
        <button
          onClick={() => navigate('/services')}
          className="mt-4 btn-secondary"
        >
          Retour à la liste des services
        </button>
      </div>
    );
  }

  const tabs = [
    {
      id: 'general',
      label: 'Informations Générales',
      icon: Shield
    },
    {
      id: 'shops',
      label: 'Shops Associés',
      icon: Store
    },
    {
      id: 'practitioners',
      label: 'Artistes Associés',
      icon: Users
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Modifier: {service.name}</span>
              <HelpIcon 
                docPath="services#modification" 
                tooltip="Documentation : Modification d'un service"
              />
            </h1>
            {hasUnsavedChanges && (
              <p className="text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded mt-1">
                Modifications non sauvegardées
              </p>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving || !hasUnsavedChanges}
        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
      >
        <Save className="w-4 h-4" />
        <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
      </button>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 mt-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <div className="space-y-8 mt-8">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className={`mr-2 h-5 w-5 ${
                    activeTab === tab.id ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`} />
                  <div className="text-left">
                    <div>{tab.label}</div>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-8">
          {/* General Information Tab */}
          {activeTab === 'general' && (
            <>
              {/* Basic Information */}
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                    <Shield className="w-5 h-5 text-primary-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-textPrimary">Informations Générales</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Nom du Service *
                    </label>
                    <input
                      type="text"
                      value={service.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Description *
                    </label>
                    <textarea
                      value={service.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={4}
                      className="input-field"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Catégorie *
                      </label>
                      <select
                        value={service.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="input-field"
                        required
                      >
                        <option value="TATTOO">Tatouage</option>
                        <option value="PIERCING">Piercing</option>
                        <option value="CONSULTATION">Consultation</option>
                        <option value="AFTERCARE">Soins</option>
                        <option value="REMOVAL">Suppression</option>
                        <option value="JEWELRY">Bijouterie</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Sous-catégorie
                      </label>
                      <input
                        type="text"
                        value={service.subcategory || ''}
                        onChange={(e) => handleInputChange('subcategory', e.target.value || undefined)}
                        className="input-field"
                        placeholder="Optionnel"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing & Duration */}
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-textPrimary">Tarification & Durée</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Prix de base (€) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={service.basePrice}
                      onChange={(e) => handleInputChange('basePrice', parseFloat(e.target.value) || 0)}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Durée estimée (min) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={service.estimatedDuration}
                      onChange={(e) => handleInputChange('estimatedDuration', parseInt(e.target.value) || 30)}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Nombre de sessions
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={service.sessionCount || ''}
                      onChange={(e) => handleInputChange('sessionCount', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="input-field"
                      placeholder="Optionnel"
                    />
                  </div>
                </div>
              </div>

              {/* Online Booking Configuration */}
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-textPrimary">Configuration Réservation en Ligne</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={service.onlineBookingEnabled}
                        onChange={(e) => handleInputChange('onlineBookingEnabled', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-textPrimary">Réservation en ligne activée</span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={service.depositRequired}
                        onChange={(e) => handleInputChange('depositRequired', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-textPrimary">Acompte requis</span>
                    </label>
                  </div>

                  {service.depositRequired && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-7">
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-2">
                          Type d'acompte
                        </label>
                        <select
                          value={service.depositType || ''}
                          onChange={(e) => handleInputChange('depositType', e.target.value || undefined)}
                          className="input-field"
                        >
                          <option value="">Sélectionner...</option>
                          <option value="FIXED">Montant fixe</option>
                          <option value="PERCENTAGE">Pourcentage</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-2">
                          Montant / Pourcentage
                        </label>
                        <input
                          type="number"
                          min="0"
                          step={service.depositType === 'PERCENTAGE' ? '1' : '0.01'}
                          max={service.depositType === 'PERCENTAGE' ? '100' : undefined}
                          value={service.depositAmount || ''}
                          onChange={(e) => handleInputChange('depositAmount', e.target.value ? parseFloat(e.target.value) : undefined)}
                          className="input-field"
                          placeholder={service.depositType === 'PERCENTAGE' ? '0-100' : 'Montant en €'}
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Jours max. de réservation à l'avance
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={service.maxAdvanceBookingDays || ''}
                      onChange={(e) => handleInputChange('maxAdvanceBookingDays', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="input-field"
                      placeholder="Optionnel (ex: 90)"
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <h2 className="text-xl font-semibold text-textPrimary">Statut</h2>
                </div>

                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={service.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-textPrimary">Service actif</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-7">
                    Les services inactifs n'apparaîtront pas dans les options de réservation
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Shops Tab */}
          {activeTab === 'shops' && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                  <Store className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Shops Associés</h2>
              </div>

              {shops.length > 0 ? (
                <div className="space-y-4">
                  {shops.map((shop) => (
                    <div key={shop.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-medium text-textPrimary">{shop.name}</h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              shop.isOffered 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {shop.isOffered ? 'Proposé' : 'Non proposé'}
                            </span>
                            {shop.isOffered && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                shop.isAvailable 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {shop.isAvailable ? 'Disponible' : 'Indisponible'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{shop.city}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Store className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>Aucun shop associé</p>
                  <p className="text-sm">Les associations se gèrent depuis la page d'édition du shop</p>
                </div>
              )}
            </div>
          )}

          {/* Practitioners Tab */}
          {activeTab === 'practitioners' && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-lg">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Artistes Associés</h2>
              </div>

              {practitioners.length > 0 ? (
                <div className="space-y-4">
                  {practitioners.map((practitioner) => (
                    <div key={practitioner.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-medium text-textPrimary">{practitioner.firstName} {practitioner.lastName}</h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              practitioner.isOffered 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {practitioner.isOffered ? 'Proposé' : 'Non proposé'}
                            </span>
                            {practitioner.isOffered && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                practitioner.isAvailable 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {practitioner.isAvailable ? 'Disponible' : 'Indisponible'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{practitioner.email}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>Aucun artiste associé</p>
                  <p className="text-sm">Les associations se gèrent depuis la page d'édition de l'artiste</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

